import { computePuzzleGrid } from './puzzleGrid';

/**
 * The coloured (master) pieces are only usable 1:1 against the outline pieces. If
 * the two arrays have different lengths they were cut on different grids (e.g.
 * 9 outline pieces and 15 master pieces), and showing master piece N inside
 * outline cell N paints a zoomed, wrongly-placed crop of another region. In that
 * case the master set is ignored (the outline is shown instead) rather than mixed.
 */
export function usableMasterUrls(outlineUrls, masterUrls) {
  const outline = Array.isArray(outlineUrls) ? outlineUrls : [];
  const master = Array.isArray(masterUrls) ? masterUrls : [];
  if (master.length === outline.length) return master;
  if (outline.length > 0 && master.length > 0) {
    console.warn(
      `[board] ${outline.length} outline pieces but ${master.length} master pieces: ignoring the master set. Re-save the puzzle in admin.`
    );
  }
  return [];
}

/**
 * What the reveal page shows in place of the board. After a refresh the theme is not loaded yet, so there
 * are no pieces for a few seconds: that must read as "loading", not as "Team artwork preview unavailable"
 * (which looks like a failure and is what players saw for ~10 s).
 *   'board'       there are pieces to draw
 *   'loading'     no theme yet and no failure so far
 *   'unavailable' the theme loaded (or failed) and there really are no pieces
 */
export function revealBoardState({ pieceCount, themeData, themeStatus }) {
  if (Number(pieceCount) > 0) return 'board';
  if (!themeData && themeStatus !== 'failed') return 'loading';
  return 'unavailable';
}

/**
 * rows x cols that holds exactly `pieceCount` cells. A requested grid whose area
 * differs from the piece count would clip (or leave empty) cells, so it is
 * replaced by the best-fitting grid for that count.
 */
export function gridForPieceCount(pieceCount, rows, cols) {
  const count = Math.floor(Number(pieceCount)) || 0;
  if (count > 0 && Number(rows) * Number(cols) !== count) {
    const grid = computePuzzleGrid(count);
    return { rows: grid.rows, cols: grid.cols };
  }
  return { rows, cols };
}

/**
 * A player's saved tile image is only valid for the cell it was drawn for. If the
 * admin re-pieced the artwork afterwards (e.g. 3x3 -> 5x3), old tiles have the old
 * piece shape; drawn into a new cell they land misaligned. Same shape (within
 * `tolerance`) means it still fits.
 */
export function aspectFits(actual, expected, tolerance = 0.12) {
  const a = Number(actual);
  const e = Number(expected);
  if (!(a > 0) || !(e > 0)) return true; // unknown yet: do not hide anything
  return Math.abs(a - e) / e <= tolerance;
}
